import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

// Check if running on native platform
const isNative = () => window.Capacitor?.isNativePlatform?.() || false;

// Helper to get token from web storage
const getStoredToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// Helper to get token from Capacitor Preferences (native only)
const getNativeToken = async () => {
  if (!isNative()) return null;
  
  try {
    // Small delay to ensure Capacitor is fully ready after app launch
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const { Preferences } = window.Capacitor.Plugins;
    if (!Preferences) {
      console.log('[Auth] Preferences plugin not available');
      return null;
    }
    
    const result = await Preferences.get({ key: 'token' });
    if (result?.value) {
      console.log('[Auth] Token found in native Preferences');
      return result.value;
    }
  } catch (e) {
    console.log('[Auth] Could not get native token:', e);
  }
  return null;
};

// Helper to clear token from all storages
const clearStoredToken = async () => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  
  if (isNative()) {
    try {
      const { Preferences } = window.Capacitor.Plugins;
      await Preferences?.remove?.({ key: 'token' });
    } catch (e) {
      console.log('[Auth] Could not clear native token:', e);
    }
  }
};

// Helper to store token (in web storage + native Preferences)
const storeToken = async (token, persistent = true) => {
  // On native, ALWAYS use persistent storage (localStorage)
  // sessionStorage gets wiped when iOS suspends the app
  const usePersistent = persistent || isNative();
  
  // Web storage
  if (usePersistent) {
    localStorage.setItem('token', token);
  } else {
    sessionStorage.setItem('token', token);
  }
  
  // Also store in Capacitor Preferences (for native - survives force quit)
  if (isNative()) {
    try {
      const { Preferences } = window.Capacitor.Plugins;
      await Preferences?.set?.({ key: 'token', value: token });
      console.log('[Auth] Token stored in native Preferences');
    } catch (e) {
      console.log('[Auth] Could not store native token:', e);
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      let token = null;
      
      // On NATIVE: Check Capacitor Preferences FIRST (survives iOS force quit)
      // This is the key fix - localStorage gets wiped on force quit, Preferences doesn't
      if (isNative()) {
        token = await getNativeToken();
      }
      
      // Fallback to web storage (or primary source on web)
      if (!token) {
        token = getStoredToken();
        if (token) {
          console.log('[Auth] Token found in web storage');
        }
      }
      
      if (token) {
        try {
          const data = await api.get('/auth/me');
          setUser(data.user);
          
          // If token came from Preferences but not localStorage, restore localStorage
          // This re-syncs web storage after force quit recovery
          if (isNative() && !getStoredToken()) {
            localStorage.setItem('token', token);
            console.log('[Auth] Re-synced token to localStorage');
          }
        } catch {
          await clearStoredToken();
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Native push notification setup
  // iOS: Swift handles FCM token registration natively
  // Android: We listen for registration event and send token to backend
  useEffect(() => {
    if (!user) return;

    const setupPush = async () => {
      // Only run on native platforms
      if (!isNative()) return;

      try {
        const { PushNotifications, Device } = window.Capacitor.Plugins;
        if (!PushNotifications) {
          console.log('[Push] PushNotifications plugin not available');
          return;
        }

        // Determine platform
        const deviceInfo = await Device.getInfo();
        const platform = deviceInfo.platform; // 'ios' or 'android'
        console.log('[Push] Platform:', platform);

        // Request permissions
        console.log('[Push] Requesting permissions...');
        const perm = await PushNotifications.requestPermissions();
        console.log('[Push] Permission result:', perm.receive);
        
        if (perm.receive !== 'granted') {
          console.log('[Push] Permission not granted, skipping registration');
          return;
        }

        // Android: Listen for registration event and send token to backend
        // iOS: Swift AppDelegate handles FCM token, so we skip this listener
        if (platform === 'android') {
          console.log('[Push] Setting up Android registration listener...');
          
          // Remove any existing listeners to avoid duplicates
          await PushNotifications.removeAllListeners();
          
          // Listen for successful registration
          PushNotifications.addListener('registration', async (tokenData) => {
            console.log('[Push] Android FCM token received:', tokenData.value?.slice(0, 30) + '...');
            try {
              await api.post('/push/register-native', {
                token: tokenData.value,
                platform: 'android'
              });
              console.log('[Push] Android token registered with backend successfully');
            } catch (err) {
              console.error('[Push] Failed to register Android token:', err);
            }
          });

          // Listen for registration errors
          PushNotifications.addListener('registrationError', (err) => {
            console.error('[Push] Registration error:', JSON.stringify(err));
          });

          // Listen for incoming notifications (foreground)
          PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('[Push] Notification received in foreground:', notification);
          });

          // Listen for notification taps
          PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            console.log('[Push] Notification action performed:', action);
          });
        }

        // Trigger registration - this kicks off the native flow
        // iOS: Swift handles the FCM token from here
        // Android: The 'registration' listener above will fire with the token
        console.log('[Push] Triggering native registration...');
        await PushNotifications.register();
        console.log('[Push] Registration triggered');

      } catch (e) {
        console.error('[Push] Setup error:', e);
      }
    };

    setupPush();
  }, [user]);

  const login = async (email, password, rememberMe = false) => {
    const data = await api.post('/auth/login', { email, password });
    // rememberMe is ignored on native - always persistent
    await storeToken(data.token, rememberMe);
    setUser(data.user);
    return data.user;
  };

  const loginWithMagicLink = async (token) => {
    const data = await api.post('/auth/verify-magic-link', { token });
    await storeToken(data.token, true);
    setUser(data.user);
    return data.user;
  };

  const requestMagicLink = async (email) => {
    return api.post('/auth/magic-link', { email });
  };

  const logout = async () => {
    await clearStoredToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginWithMagicLink, requestMagicLink, logout,
      isOwner: user?.role === 'OWNER',
      isAdmin: user?.role === 'OWNER' || user?.role === 'ADMIN',
      isAccountant: ['OWNER', 'ADMIN', 'ACCOUNTANT'].includes(user?.role),
      isContractor: user?.role === 'CONTRACTOR',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
